import axios from 'axios';
import {reportsURL} from '@app/constants/ApiEndPoints';

/**
 * Reports API client.
 *
 * Every report shares one request shape and one response shape, so this single
 * function serves all of them — a report is identified only by its slug.
 */

export interface ReportFilterValues {
  /** 'year' sends year + period; 'range' sends from + to. Never both. */
  mode?: 'year' | 'range';
  year?: number;
  /** Slice of the year: q1-q4, h1, h2, or full. */
  period?: 'q1' | 'q2' | 'q3' | 'q4' | 'h1' | 'h2' | 'full';
  from?: string;
  to?: string;
  franchiseId?: string | number | Array<string | number>;
  /** Growth reports: which source dimension to group by. */
  source?: 'found_via' | 'got_in_touch';
  /**
   * Finer-grained filters. Each report shows only the ones its data holds;
   * values mirror the allowed lists in services/reports/filters.js (fms-api).
   */
  subjectId?: number;
  studentId?: number;
  itemType?: string;
  orderStatus?: string;
  paymentSource?: string;
  printType?: string;
  workOrderStatus?: string;
  /** Student Subject Orders: paid | unpaid | no_order. */
  paymentStatus?: string;
  /** Growth reports: an enquiry child (lead_inquiry_students.id). */
  leadStudentId?: number;
  /** Growth reports: current status slug — "Status" / "Stuck at". */
  leadStatus?: string;
  /** Source Effectiveness: how they found us. */
  foundViaId?: number;
  /** Source Effectiveness: contact method (how they got in touch). */
  gotInTouchId?: number;
  page?: number;
  limit?: number;
}

export interface ReportKpi {
  key: string;
  label: string;
  value: number;
  format: 'currency' | 'number' | 'percent';
  detail?: string;
}

export interface ReportPayload {
  success: boolean;
  collections: {
    filters_applied: Record<string, any>;
    kpis: ReportKpi[];
    series: Record<string, any[]>;
    detail: {rows: any[]; total: number; page: number; limit: number} | null;
  };
}

const toQuery = (filters: ReportFilterValues): string => {
  const params = new URLSearchParams();

  // Only the active mode's parameters are sent, so the API can never receive a
  // year and a conflicting date range at the same time.
  if (filters.mode === 'range') {
    if (filters.from) params.append('from', filters.from);
    if (filters.to) params.append('to', filters.to);
  } else if (filters.year) {
    params.append('year', String(filters.year));
    params.append('period', filters.period || 'full');
  }

  if (Array.isArray(filters.franchiseId)) {
    if (filters.franchiseId.length) {
      params.append('franchise_id', filters.franchiseId.join(','));
    }
  } else if (filters.franchiseId) {
    params.append('franchise_id', String(filters.franchiseId));
  }

  if (filters.source) params.append('source', filters.source);

  if (filters.subjectId) params.append('subject_id', String(filters.subjectId));
  if (filters.studentId) params.append('student_id', String(filters.studentId));
  if (filters.itemType) params.append('item_type', filters.itemType);
  if (filters.orderStatus) params.append('order_status', filters.orderStatus);
  if (filters.paymentSource) params.append('payment_source', filters.paymentSource);
  if (filters.printType) params.append('print_type', filters.printType);
  if (filters.workOrderStatus) params.append('work_order_status', filters.workOrderStatus);
  if (filters.paymentStatus) params.append('payment_status', filters.paymentStatus);
  if (filters.leadStudentId) params.append('lead_student_id', String(filters.leadStudentId));
  if (filters.leadStatus) params.append('lead_status', filters.leadStatus);
  if (filters.foundViaId) params.append('found_via_id', String(filters.foundViaId));
  if (filters.gotInTouchId) params.append('got_in_touch_id', String(filters.gotInTouchId));

  params.append('page', String(filters.page ?? 1));
  params.append('limit', String(filters.limit ?? 50));

  return params.toString();
};

export const getReport = async (
  slug: string,
  filters: ReportFilterValues = {}
): Promise<ReportPayload | any> => {
  try {
    const response = await axios.get(`${reportsURL}/${slug}?${toQuery(filters)}`);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error: any) {
    return error?.response?.data;
  }
};

/**
 * Lead-students (enquiry children) for one franchise, for the Growth reports'
 * student filter. Not the students list — most lead-students never enrol.
 */
export const getLeadStudents = async (franchiseId: number): Promise<any> => {
  try {
    const response = await axios.get(`${reportsURL}/lead-students?franchise_id=${franchiseId}`);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error: any) {
    return error?.response?.data;
  }
};
